"use client";

import { AnimatePresence, motion } from "motion/react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { cn } from "../../lib/utils";
import { Heading } from "../heading";
import { Image } from "../media/image";

type Testimonial = {
  name: string;
  designation: string;
  quote: string;
  image: string | null;
  alt: string | null;
};

export function TestimonyCarousel({
  title,
  testimonials,
  autoplay = false,
  textColorClasses,
  ...additionalProperties
}: {
  title: string | undefined;
  testimonials: Testimonial[];
  autoplay?: boolean;
  textColorClasses?: string;
}) {
  const [active, setActive] = useState(0);

  const handleNext = useCallback(() => {
    setActive((previous) => (previous + 1) % testimonials.length);
  }, [testimonials.length]);

  const handlePrevious = useCallback(() => {
    setActive(
      (previous) => (previous - 1 + testimonials.length) % testimonials.length,
    );
  }, [testimonials.length]);

  const isActive = (index: number) => {
    return index === active;
  };

  useEffect(() => {
    if (autoplay) {
      const interval = setInterval(handleNext, 5000);
      return () => clearInterval(interval);
    }
  }, [autoplay, handleNext]);

  // Generate a random rotation angle for each testimonial only once,
  // and keep it stable across renders.
  const [rotateAngles] = useState(() =>
    testimonials.map(() => Math.floor(Math.random() * 21) - 10),
  );

  const getRotateY = (index: number) => rotateAngles[index];

  return (
    <div
      {...additionalProperties}
      className={cn(
        "mx-auto max-w-sm px-4 md:max-w-4xl md:px-8 lg:px-12",
        textColorClasses,
      )}
    >
      {title && (
        <Heading variant="h2" className="mb-8 text-left">
          {title}
        </Heading>
      )}
      <div className="relative grid grid-cols-1 gap-20 md:grid-cols-2">
        <div>
          <div className="relative h-80 w-full">
            <AnimatePresence>
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={testimonial.image ?? index}
                  initial={{
                    opacity: 0,
                    scale: 0.95,
                    rotate: getRotateY(index),
                  }}
                  animate={{
                    opacity: isActive(index) ? 1 : 0.6,
                    scale: isActive(index) ? 1 : 0.97,
                    rotate: isActive(index) ? 0 : getRotateY(index),
                    zIndex: isActive(index)
                      ? 999
                      : testimonials.length + 2 - index,
                    y: isActive(index) ? 0 : 0,
                  }}
                  exit={{
                    opacity: 0,
                    scale: 0.95,
                    rotate: getRotateY(active),
                  }}
                  transition={{
                    duration: 0.35,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                  className="absolute inset-0 origin-bottom"
                  style={{
                    willChange: "transform, opacity",
                    backfaceVisibility: "hidden",
                    WebkitBackfaceVisibility: "hidden",
                  }}
                >
                  <Image
                    src={testimonial.image!}
                    alt={testimonial.alt!}
                    width={500}
                    height={500}
                    draggable={false}
                    className="h-full w-full rounded-3xl object-cover object-center"
                  />
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </div>
        <div className="flex flex-col justify-between py-4">
          <motion.div
            key={active}
            initial={{
              y: 12,
              opacity: 0,
            }}
            animate={{
              y: 0,
              opacity: 1,
            }}
            exit={{
              y: -12,
              opacity: 0,
            }}
            transition={{
              duration: 0.25,
              ease: [0.22, 1, 0.36, 1],
            }}
            style={{ willChange: "opacity, transform" }}
          >
            <h3 className={cn("text-2xl font-bold", textColorClasses)}>
              {testimonials[active].name}
            </h3>
            <p className={cn("text-sm", textColorClasses)}>
              {testimonials[active].designation}
            </p>
            <motion.p className={cn("mt-8 text-lg", textColorClasses)}>
              {testimonials[active].quote.split(" ").map((word, index) => (
                <motion.span
                  key={index}
                  initial={{
                    opacity: 0,
                    y: 8,
                  }}
                  animate={{
                    opacity: 1,
                    y: 0,
                  }}
                  transition={{
                    duration: 0.2,
                    ease: [0.22, 1, 0.36, 1],
                    delay: 0.015 * index,
                  }}
                  className="inline-block"
                  style={{ willChange: "opacity, transform" }}
                >
                  {word}&nbsp;
                </motion.span>
              ))}
            </motion.p>
          </motion.div>
          <div className="flex gap-4 pt-12 md:pt-0">
            <button
              onClick={handlePrevious}
              className="bg-secondary group/button flex h-7 w-7 items-center justify-center rounded-full cursor-pointer"
            >
              <ArrowLeft className="text-foreground h-5 w-5 transition-transform duration-300 group-hover/button:rotate-12" />
            </button>
            <button
              onClick={handleNext}
              className="bg-secondary group/button flex h-7 w-7 items-center justify-center rounded-full cursor-pointer"
            >
              <ArrowRight className="text-foreground h-5 w-5 transition-transform duration-300 group-hover/button:-rotate-12" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
